import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  StarIcon,
  GiftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  SparklesIcon,
  TruckIcon,
  MagnifyingGlassPlusIcon,
  ArrowUpTrayIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import { Users, Phone, Home, CheckCircle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import ModalFooter from '../ui/ModalFooter';
import EmptyState from '../ui/EmptyState';
import PageHeader from '../ui/PageHeader';
import Input from '../ui/Input';
import Checkbox from '../ui/Checkbox';
import Label from '../ui/Label';
import { useConfirmDialog } from '../ui/ConfirmDialog';
import { ListPageSkeleton } from '../ui/PageSkeleton';
import RewardCardVisual from '../shared/RewardCardVisual';
import ImageLightbox from '../ui/ImageLightbox';
import { getRewardImage } from '../../utils/rewards';
import { validateFields, validateRequired } from '../../utils/formValidation';

const EMPTY_REWARD = {
  name: '',
  description: '',
  points_required: 0,
  image_url: '',
  is_welcome_choice: false,
};

function companyRequestConfig(companyId) {
  return companyId ? { params: { company_id: companyId } } : {};
}

function withCompanyCredentials(companyId) {
  return { withCredentials: true, ...companyRequestConfig(companyId) };
}

const DEFAULT_DELIVERY_SETTINGS = {
  vendor_delivery_days: 7,
  vendor_overdue_day: 5,
  client_delivery_days: 7,
};

function RewardProductImageField({ imageUrl, onChange, t, showExpandHint = false }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t.rewards.imageInvalidType || 'Please choose a valid image file.');
      event.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t.rewards.imageTooLarge || 'Image is too large. Maximum size is 5 MB.');
      event.target.value = '';
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await axios.post('/api/admin/rewards/images', formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange({ image_url: response.data.image_url });
      toast.success(t.rewards.imageUploaded || 'Image uploaded successfully.');
    } catch (error) {
      toast.error(error.response?.data?.error || t.rewards.imageUploadFailed || 'Could not upload image.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div>
      <Label>{t.rewards.imageSection || t.rewards.imageUrl}</Label>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
        <div className="min-w-0 flex-1">
          <Input
            type="text"
            value={imageUrl || ''}
            onChange={(e) => onChange({ image_url: e.target.value })}
            placeholder={t.rewards.imageUrlPlaceholder}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          icon={ArrowUpTrayIcon}
          className="shrink-0"
        >
          {uploading
            ? (t.rewards.imageUploading || 'Uploading...')
            : (t.rewards.imageUpload || 'Upload from device')}
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="sr-only"
        onChange={handleImageUpload}
      />
      <p className="mt-1.5 text-xs text-secondary-500 ">
        {t.rewards.imageUploadHint || 'Upload a file or paste an image URL. PNG, JPG, WEBP, or GIF up to 5 MB.'}
      </p>
      {showExpandHint ? (
        <p className="mt-1 text-xs text-orange-600/90 ">
          {t.rewards.formPreviewExpandHint || 'Click the preview to view the full image.'}
        </p>
      ) : null}
    </div>
  );
}

function RewardFormFields({ reward, onChange, t, layout = 'default', errors = {} }) {
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const showPreview = Boolean(reward.image_url || reward.name);
  const isModal = layout === 'modal';
  const productImage = getRewardImage(reward);

  if (isModal) {
    return (
      <div className="space-y-5">
        {showPreview ? (
          <section className="rounded-2xl border border-secondary-200/70 bg-secondary-50/40 p-4  ">
            <p className="eyebrow mb-3">{t.rewards.formPreview || 'Card preview'}</p>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <button
                type="button"
                onClick={() => setImageViewerOpen(true)}
                aria-label={t.rewards.formPreviewExpand || 'View full image'}
                className="group relative mx-auto h-24 w-full max-w-[9.5rem] shrink-0 cursor-zoom-in overflow-hidden rounded-xl border border-secondary-200/80 bg-white shadow-soft transition hover:border-orange-300/80 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2     sm:mx-0"
              >
                <RewardCardVisual reward={reward} className="h-full w-full" />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-secondary-950/0 transition group-hover:bg-secondary-950/25 group-focus-visible:bg-secondary-950/25">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-secondary-700 opacity-0 shadow-sm transition group-hover:opacity-100 group-focus-visible:opacity-100  ">
                    <MagnifyingGlassPlusIcon className="h-4 w-4" aria-hidden />
                  </span>
                </span>
              </button>
              <div className="min-w-0 flex-1">
                <RewardProductImageField
                  imageUrl={reward.image_url}
                  onChange={onChange}
                  t={t}
                  showExpandHint
                />
              </div>
            </div>
          </section>
        ) : null}

        <ImageLightbox
          isOpen={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          src={productImage}
          alt={reward.name || t.rewards.formImageViewTitle || 'Reward image'}
          title={reward.name || undefined}
        >
          <div className="mx-auto aspect-[16/10] w-full max-w-2xl overflow-hidden rounded-2xl border border-secondary-200/80 bg-white shadow-premium  ">
            <RewardCardVisual reward={reward} className="h-full w-full" />
          </div>
        </ImageLightbox>

        <section>
          <p className="eyebrow mb-3">{t.rewards.formDetails || 'Reward details'}</p>
          <div className="space-y-4">
            {!showPreview ? (
              <RewardProductImageField
                imageUrl={reward.image_url}
                onChange={onChange}
                t={t}
              />
            ) : null}

            <div>
              <Label required>{t.rewards.rewardName}</Label>
              <Input
                type="text"
                value={reward.name}
                onChange={(e) => onChange({ name: e.target.value })}
                placeholder={t.rewards.rewardNamePlaceholder}
                className={errors.name ? 'border-error-500 focus:border-error-500 focus:ring-error-500/30' : ''}
                aria-invalid={errors.name ? true : undefined}
              />
              {errors.name ? <p className="mt-1.5 text-xs font-medium text-error-600">{errors.name}</p> : null}
            </div>

            <div>
              <Label>{t.rewards.description}</Label>
              <Input
                as="textarea"
                variant="textarea"
                value={reward.description || ''}
                onChange={(e) => onChange({ description: e.target.value })}
                rows="3"
                placeholder={t.rewards.describeReward}
              />
            </div>

            <div>
              <Label required>{t.rewards.pointsRequired}</Label>
              <Input
                type="number"
                value={reward.points_required}
                onChange={(e) => onChange({ points_required: parseInt(e.target.value, 10) || 0 })}
                placeholder="0"
                className={errors.points_required ? 'border-error-500 focus:border-error-500 focus:ring-error-500/30' : ''}
                aria-invalid={errors.points_required ? true : undefined}
              />
              {errors.points_required ? (
                <p className="mt-1.5 text-xs font-medium text-error-600">{errors.points_required}</p>
              ) : null}
            </div>

            <Checkbox
              id={`welcome-choice-${reward.id || 'new'}`}
              checked={!!reward.is_welcome_choice}
              onChange={(checked) => onChange({ is_welcome_choice: checked })}
              label={t.rewards.welcomeChoiceLabel}
            />
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      {showPreview ? (
        <div className="overflow-hidden rounded-xl border border-secondary-200 ">
          <div className="aspect-[16/10] bg-secondary-100 ">
            <RewardCardVisual reward={reward} />
          </div>
        </div>
      ) : null}

      <div>
        <Label required>{t.rewards.rewardName}</Label>
        <Input
          type="text"
          value={reward.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={t.rewards.rewardNamePlaceholder}
          className={errors.name ? 'border-error-500 focus:border-error-500 focus:ring-error-500/30' : ''}
          aria-invalid={errors.name ? true : undefined}
        />
        {errors.name ? <p className="mt-1.5 text-xs font-medium text-error-600">{errors.name}</p> : null}
      </div>

      <div>
        <Label>{t.rewards.description}</Label>
        <Input
          as="textarea"
          variant="textarea"
          value={reward.description || ''}
          onChange={(e) => onChange({ description: e.target.value })}
          rows="3"
          placeholder={t.rewards.describeReward}
        />
      </div>

      <RewardProductImageField
        imageUrl={reward.image_url}
        onChange={onChange}
        t={t}
      />

      <div>
        <Label required>{t.rewards.pointsRequired}</Label>
        <Input
          type="number"
          value={reward.points_required}
          onChange={(e) => onChange({ points_required: parseInt(e.target.value, 10) || 0 })}
          placeholder="0"
          className={errors.points_required ? 'border-error-500 focus:border-error-500 focus:ring-error-500/30' : ''}
          aria-invalid={errors.points_required ? true : undefined}
        />
        {errors.points_required ? (
          <p className="mt-1.5 text-xs font-medium text-error-600">{errors.points_required}</p>
        ) : null}
      </div>

      <Checkbox
        id={`welcome-choice-inline-${reward.id || 'draft'}`}
        checked={!!reward.is_welcome_choice}
        onChange={(checked) => onChange({ is_welcome_choice: checked })}
        label={t.rewards.welcomeChoiceLabel}
      />
    </>
  );
}

function RewardModalShell({ children }) {
  return <div className="space-y-5">{children}</div>;
}

function RewardsConfig({ presetCompanyId = null }) {
  const { t } = useLanguage();
  const { confirm, dialog } = useConfirmDialog();
  const { user } = useAuth();
  const sc = t.settings?.systemConfig || {};
  const isPlatformAdmin = user?.is_admin && !user?.company_id;
  const activeCompanyId = user?.company_id || presetCompanyId || null;
  const [, setPointConfigs] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConfigs, setEditingConfigs] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [newReward, setNewReward] = useState(EMPTY_REWARD);
  const [rewardFieldErrors, setRewardFieldErrors] = useState({});
  const [deliverySettings, setDeliverySettings] = useState(DEFAULT_DELIVERY_SETTINGS);
  const [localDeliverySettings, setLocalDeliverySettings] = useState(DEFAULT_DELIVERY_SETTINGS);
  const [editingDeliverySettings, setEditingDeliverySettings] = useState(false);
  const [shortActionConfigs, setShortActionConfigs] = useState([]);
  const [localShortActions, setLocalShortActions] = useState({});
  const [editingShortActions, setEditingShortActions] = useState(false);
  const [featureUnavailable, setFeatureUnavailable] = useState(false);

  // Local state for point configs editing
  const [localConfigs, setLocalConfigs] = useState({
    welcome_bonus: { points: 100, description: '' },
    affiliated: { points: 10, description: '' },
    contacted: { points: 25, description: '' },
    visited: { points: 50, description: '' },
    completed: { points: 100, description: '' }
  });

  const fetchData = useCallback(async () => {
    if (isPlatformAdmin && !activeCompanyId) {
      setLoading(false);
      setFeatureUnavailable(false);
      return;
    }
    try {
      setLoading(true);
      setFeatureUnavailable(false);
      const requestConfig = withCompanyCredentials(activeCompanyId);
      const [configsRes, rewardsRes, deliveryRes, shortActionsRes] = await Promise.all([
        axios.get('/api/admin/reward-configs', requestConfig),
        axios.get('/api/admin/rewards', requestConfig),
        axios.get('/api/admin/reward-delivery-settings', requestConfig),
        axios.get('/api/admin/short-action-configs', requestConfig),
      ]);

      setPointConfigs(configsRes.data);
      setRewards(rewardsRes.data);
      setDeliverySettings(deliveryRes.data);
      setLocalDeliverySettings(deliveryRes.data);

      const shortList = shortActionsRes.data || [];
      setShortActionConfigs(shortList);
      const shortMap = {};
      shortList.forEach((config) => {
        shortMap[config.action_key] = {
          points: config.points,
          is_active: config.is_active,
          reward_tier: config.reward_tier,
          description: config.description || '',
        };
      });
      setLocalShortActions(shortMap);

      // Initialize local configs from fetched data
      const configsMap = {};
      configsRes.data.forEach(config => {
        configsMap[config.state_name] = {
          points: config.points,
          description: config.description || ''
        };
      });
      setLocalConfigs(prev => ({ ...prev, ...configsMap }));
    } catch (error) {
      console.error('Error fetching rewards data:', error);
      if (error.response?.data?.error_key === 'featureNotAvailableForCompany') {
        setFeatureUnavailable(true);
      } else {
        toast.error(t.rewards.failedToLoad);
      }
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, isPlatformAdmin, t.rewards.failedToLoad]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const savePointConfigs = async () => {
    try {
      const configs = Object.entries(localConfigs).map(([state_name, data]) => ({
        state_name,
        points: data.points,
        description: data.description
      }));

      await axios.post('/api/admin/reward-configs', { configs }, withCompanyCredentials(activeCompanyId));
      toast.success(t.rewards.savedSuccessfully);
      setEditingConfigs(false);
      fetchData();
    } catch (error) {
      console.error('Error saving configs:', error);
      toast.error(t.rewards.failedToSave);
    }
  };

  const saveShortActionConfigs = async () => {
    try {
      const configs = Object.entries(localShortActions).map(([action_key, data]) => ({
        action_key,
        points: data.points,
        is_active: data.is_active,
      }));

      await axios.post('/api/admin/short-action-configs', { configs }, withCompanyCredentials(activeCompanyId));
      toast.success(t.rewards.savedSuccessfully);
      setEditingShortActions(false);
      fetchData();
    } catch (error) {
      console.error('Error saving short action configs:', error);
      toast.error(t.rewards.failedToSave);
    }
  };

  const renderShortActionCard = (config) => {
    const sa = t.enrola?.shortActions || {};
    const label = sa.actionLabels?.[config.action_key] || config.action_key;
    const desc = sa.actionDescriptions?.[config.action_key] || config.description;
    const local = localShortActions[config.action_key];
    const isActive = local?.is_active ?? config.is_active;
    const points = local?.points ?? config.points;

    return (
      <motion.div
        key={config.action_key}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl p-4 border ${
          !isActive
            ? 'border-secondary-200/40 bg-secondary-100/40 opacity-75  '
            : config.reward_tier === 'high'
              ? 'border-amber-300/60 bg-gradient-to-br from-amber-50/50 to-orange-50/30   '
              : 'border-secondary-200/50 bg-secondary-50/30  '
        }`}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h4 className="font-semibold text-secondary-900 ">{label}</h4>
            <p className="text-xs text-secondary-600 ">{desc}</p>
            {config.reward_tier === 'high' ? (
              <span className="mt-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800  ">
                {sa.highReward}
              </span>
            ) : null}
          </div>
          {editingShortActions ? (
            <Checkbox
              id={`short-action-active-${config.action_key}`}
              checked={isActive}
              onChange={(checked) => setLocalShortActions((prev) => ({
                ...prev,
                [config.action_key]: {
                  ...prev[config.action_key],
                  is_active: checked,
                },
              }))}
              label={t.rewards.active}
            />
          ) : (
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
              isActive
                ? 'bg-emerald-100 text-emerald-800  '
                : 'bg-secondary-200 text-secondary-600  '
            }`}
            >
              {isActive ? t.rewards.active : t.rewards.inactive}
            </span>
          )}
        </div>
        <label className="block text-sm font-medium text-secondary-700  mb-1">
          {t.rewards.rewardPoints}
        </label>
        {editingShortActions ? (
          <input
            type="number"
            min="0"
            value={points}
            onChange={(e) => setLocalShortActions((prev) => ({
              ...prev,
              [config.action_key]: {
                ...prev[config.action_key],
                points: parseInt(e.target.value, 10) || 0,
              },
            }))}
            className="input-field max-w-xs"
          />
        ) : (
          <div className={`text-2xl font-bold ${isActive ? 'text-orange-600' : 'text-secondary-400'}`}>
            {points} {t.rewards.points}
          </div>
        )}
      </motion.div>
    );
  };

  const saveDeliverySettings = async () => {
    if (localDeliverySettings.vendor_overdue_day > localDeliverySettings.vendor_delivery_days) {
      toast.error(t.rewards.deliveryOverdueDayInvalid);
      return;
    }

    try {
      await axios.put('/api/admin/reward-delivery-settings', localDeliverySettings, withCompanyCredentials(activeCompanyId));
      toast.success(t.rewards.deliverySettingsSaved);
      setEditingDeliverySettings(false);
      fetchData();
    } catch (error) {
      console.error('Error saving delivery settings:', error);
      toast.error(error.response?.data?.error || t.rewards.deliverySettingsFailed);
    }
  };

  const validateRewardPayload = (reward) => {
    const validation = t.ux?.validation || {};
    return validateFields({
      name: validateRequired(reward.name, validation.rewardNameRequired || t.rewards.fillRequiredFields),
      points_required:
        reward.points_required > 0
          ? null
          : (validation.pointsRequired || t.rewards.fillRequiredFields),
    });
  };

  const handleCreateReward = async () => {
    const { valid, errors } = validateRewardPayload(newReward);
    setRewardFieldErrors(errors);
    if (!valid) return;

    try {
      await axios.post('/api/admin/rewards', newReward, withCompanyCredentials(activeCompanyId));
      toast.success(t.rewards.createdSuccessfully);
      setShowRewardModal(false);
      setNewReward(EMPTY_REWARD);
      setRewardFieldErrors({});
      fetchData();
    } catch (error) {
      console.error('Error creating reward:', error);
      toast.error(t.rewards.failedToCreate);
    }
  };

  const handleUpdateReward = async () => {
    const { valid, errors } = validateRewardPayload(editingReward);
    setRewardFieldErrors(errors);
    if (!valid) return;

    try {
      await axios.put(`/api/admin/rewards/${editingReward.id}`, editingReward, withCompanyCredentials(activeCompanyId));
      toast.success(t.rewards.updatedSuccessfully);
      setEditingReward(null);
      setRewardFieldErrors({});
      fetchData();
    } catch (error) {
      console.error('Error updating reward:', error);
      toast.error(t.rewards.failedToUpdate);
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (!(await confirm({
      message: t.rewards.confirmDeleteReward,
      title: t.common.confirmDestructiveTitle,
      confirmLabel: t.common.delete,
    }))) return;

    try {
      await axios.delete(`/api/admin/rewards/${rewardId}`, withCompanyCredentials(activeCompanyId));
      toast.success(t.rewards.deletedSuccessfully);
      fetchData();
    } catch (error) {
      console.error('Error deleting reward:', error);
      toast.error(t.rewards.failedToDelete);
    }
  };

  const stateLabels = {
    welcome_bonus: { label: t.rewards.welcomeBonus, IconComponent: SparklesIcon, description: t.rewards.welcomeBonusDesc },
    affiliated: { label: t.admin.affiliated, IconComponent: Users, description: t.rewards.whenClientRefers },
    contacted: { label: t.admin.contacted, IconComponent: Phone, description: t.rewards.whenSellerContacts },
    visited: { label: t.admin.visited, IconComponent: Home, description: t.rewards.whenSellerVisits },
    completed: { label: t.admin.completed, IconComponent: CheckCircle, description: t.rewards.whenSaleCompleted }
  };

  const welcomeBonusState = 'welcome_bonus';
  const referralStateKeys = ['affiliated', 'contacted', 'visited', 'completed'];

  if (loading && (!isPlatformAdmin || activeCompanyId)) {
    return <ListPageSkeleton rows={5} />;
  }

  if (isPlatformAdmin && !activeCompanyId) {
    return (
      <div className="space-y-6 xl:space-y-7">
        <PageHeader
          eyebrow="Admin"
          icon={GiftIcon}
          title={t.rewards.rewardsConfiguration}
          subtitle={t.rewards.configureRewardPoints}
        />
        <p className="text-sm text-secondary-600 ">
          {sc.selectCompanyPrompt || t.platformAdmin?.pickCompanyFirst || 'Select a company to manage rewards.'}
        </p>
      </div>
    );
  }

  if (featureUnavailable) {
    return (
      <div className="space-y-6 xl:space-y-7">
        <PageHeader
          eyebrow="Admin"
          icon={GiftIcon}
          title={t.rewards.rewardsConfiguration}
          subtitle={t.rewards.configureRewardPoints}
        />
        <p className="rounded-xl border border-dashed border-secondary-300 p-6 text-sm text-secondary-600  ">
          {t.platformAdmin?.elevateRewardsUnavailable || t.rewards.failedToLoad}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 xl:space-y-7">
      {dialog}
      <PageHeader
        eyebrow="Admin"
        icon={GiftIcon}
        title={t.rewards.rewardsConfiguration}
        subtitle={t.rewards.configureRewardPoints}
      />

      {/* Welcome Bonus */}
      <div className="card-premium p-6 max-lg:p-4 border-2 border-orange-200/60 bg-gradient-to-br from-orange-50/40 to-amber-50/20   ">
        <div className="mb-4 flex flex-col gap-3 max-lg:items-stretch lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center ">
              <SparklesIcon className="w-5 h-5 text-orange-500 mr-2" />
              {t.rewards.welcomeBonusSection}
            </h3>
            <p className="text-sm text-secondary-600 mt-1 ">
              {t.rewards.welcomeBonusSectionDesc}
            </p>
          </div>
          {!editingConfigs ? (
            <Button variant="secondary" size="default" onClick={() => setEditingConfigs(true)} icon={PencilIcon}>
              {t.rewards.editPoints}
            </Button>
          ) : null}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-orange-200/60 bg-white/80 p-4  "
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <SparklesIcon className="w-6 h-6 text-orange-600" />
              <div>
                <h4 className="font-semibold text-secondary-900 ">{t.rewards.welcomeBonus}</h4>
                <p className="text-xs text-secondary-600 ">{t.rewards.welcomeBonusDesc}</p>
              </div>
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-secondary-700  mb-1">
              {t.rewards.rewardPoints}
            </label>
            {editingConfigs ? (
              <input
                type="number"
                min="0"
                value={localConfigs[welcomeBonusState]?.points || 0}
                onChange={(e) => setLocalConfigs(prev => ({
                  ...prev,
                  [welcomeBonusState]: { ...prev[welcomeBonusState], points: parseInt(e.target.value, 10) || 0 }
                }))}
                className="input-field max-w-xs"
              />
            ) : (
              <div className="text-2xl font-bold text-orange-600">
                {localConfigs[welcomeBonusState]?.points || 0} {t.rewards.points}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Point Configurations */}
      <div className="card-premium p-6 max-lg:p-4">
        <div className="mb-6 flex flex-col gap-3 max-lg:items-stretch lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
              <StarIcon className="w-5 h-5 text-amber-500 mr-2" />
              {t.rewards.stateRewardPoints}
            </h3>
            <p className="text-sm text-secondary-600 mt-1">
              {t.rewards.configurePointsAwarded}
            </p>
          </div>
          {!editingConfigs ? (
            <Button variant="secondary" size="default" onClick={() => setEditingConfigs(true)} icon={PencilIcon}>
              {t.rewards.editPoints}
            </Button>
          ) : (
            <div className="flex flex-col gap-2 max-lg:w-full lg:flex-row lg:gap-2">
              <Button variant="ghost" size="default" onClick={() => {
                setEditingConfigs(false);
                fetchData();
              }} icon={XMarkIcon}>
                {t.rewards.cancel}
              </Button>
              <Button variant="primary" size="default" onClick={savePointConfigs} icon={CheckIcon}>
                {t.rewards.save}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {referralStateKeys.map((state) => {
            const info = stateLabels[state];
            return (
            <motion.div
              key={state}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-br from-secondary-50/50 to-orange-50/30 rounded-xl p-4 border border-secondary-200/50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <info.IconComponent className="w-6 h-6 text-orange-600" />
                  <div>
                    <h4 className="font-semibold text-secondary-900">{info.label}</h4>
                    <p className="text-xs text-secondary-600">{info.description}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <label className="block text-sm font-medium text-secondary-700  mb-1">
                  {t.rewards.rewardPoints}
                </label>
                {editingConfigs ? (
                  <input
                    type="number"
                    value={localConfigs[state]?.points || 0}
                    onChange={(e) => setLocalConfigs(prev => ({
                      ...prev,
                      [state]: { ...prev[state], points: parseInt(e.target.value) || 0 }
                    }))}
                    className="input-field"
                  />
                ) : (
                  <div className="text-2xl font-bold text-orange-600">
                    {localConfigs[state]?.points || 0} {t.rewards.points}
                  </div>
                )}
              </div>
            </motion.div>
          )})}
        </div>
      </div>

      {/* Short Actions Points */}
      <div className="card-premium p-6 max-lg:p-4">
        <div className="mb-6 flex flex-col gap-3 max-lg:items-stretch lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center ">
              <BoltIcon className="w-5 h-5 text-orange-500 mr-2" />
              {t.rewards.shortActionsSection}
            </h3>
            <p className="text-sm text-secondary-600 mt-1 ">
              {t.rewards.shortActionsSectionDesc}
            </p>
            <p className="text-xs text-secondary-500 mt-1 ">
              {t.rewards.shortActionsVendorNote}
            </p>
          </div>
          {!editingShortActions ? (
            <Button variant="secondary" size="default" onClick={() => setEditingShortActions(true)} icon={PencilIcon}>
              {t.rewards.editPoints}
            </Button>
          ) : (
            <div className="flex flex-col gap-2 max-lg:w-full lg:flex-row lg:gap-2">
              <Button variant="ghost" size="default" onClick={() => {
                setEditingShortActions(false);
                fetchData();
              }} icon={XMarkIcon}>
                {t.rewards.cancel}
              </Button>
              <Button variant="primary" size="default" onClick={saveShortActionConfigs} icon={CheckIcon}>
                {t.rewards.save}
              </Button>
            </div>
          )}
        </div>

        <div className="mb-4">
          <p className="micro text-orange-600 ">
            {t.rewards.clientShortActionsLabel}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {shortActionConfigs
            .filter((config) => config.target_role === 'client')
            .map((config) => renderShortActionCard(config))}
        </div>

        <div className="mb-4 mt-8">
          <p className="micro text-orange-600 ">
            {t.rewards.vendorShortActionsLabel}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {shortActionConfigs
            .filter((config) => config.target_role === 'vendor')
            .map((config) => renderShortActionCard(config))}
        </div>
      </div>

      {/* Delivery Settings */}
      <div className="card-premium p-6 max-lg:p-4">
        <div className="mb-6 flex flex-col gap-3 max-lg:items-stretch lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center ">
              <TruckIcon className="w-5 h-5 text-orange-500 mr-2" />
              {t.rewards.deliverySettingsTitle}
            </h3>
            <p className="text-sm text-secondary-600 mt-1 ">
              {t.rewards.deliverySettingsDesc}
            </p>
          </div>
          {!editingDeliverySettings ? (
            <Button variant="secondary" size="default" onClick={() => setEditingDeliverySettings(true)} icon={PencilIcon}>
              {t.rewards.editDeliverySettings}
            </Button>
          ) : (
            <div className="flex flex-col gap-2 max-lg:w-full lg:flex-row lg:gap-2">
              <Button variant="ghost" size="default" onClick={() => {
                setEditingDeliverySettings(false);
                setLocalDeliverySettings(deliverySettings);
              }} icon={XMarkIcon}>
                {t.rewards.cancel}
              </Button>
              <Button variant="primary" size="default" onClick={saveDeliverySettings} icon={CheckIcon}>
                {t.rewards.save}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="rounded-xl border border-secondary-200/60 bg-secondary-50/50 p-4  ">
            <label className="block text-sm font-medium text-secondary-700  mb-1">
              {t.rewards.vendorDeliveryDaysLabel}
            </label>
            <p className="text-xs text-secondary-500  mb-3">{t.rewards.vendorDeliveryDaysDesc}</p>
            {editingDeliverySettings ? (
              <input
                type="number"
                min="1"
                value={localDeliverySettings.vendor_delivery_days}
                onChange={(e) => setLocalDeliverySettings((prev) => ({
                  ...prev,
                  vendor_delivery_days: parseInt(e.target.value, 10) || 1,
                }))}
                className="input-field"
              />
            ) : (
              <div className="text-2xl font-bold text-orange-600">
                {deliverySettings.vendor_delivery_days} {t.rewards.daysUnit}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-secondary-200/60 bg-secondary-50/50 p-4  ">
            <label className="block text-sm font-medium text-secondary-700  mb-1">
              {t.rewards.vendorOverdueDayLabel}
            </label>
            <p className="text-xs text-secondary-500  mb-3">{t.rewards.vendorOverdueDayDesc}</p>
            {editingDeliverySettings ? (
              <input
                type="number"
                min="1"
                max={localDeliverySettings.vendor_delivery_days}
                value={localDeliverySettings.vendor_overdue_day}
                onChange={(e) => setLocalDeliverySettings((prev) => ({
                  ...prev,
                  vendor_overdue_day: parseInt(e.target.value, 10) || 1,
                }))}
                className="input-field"
              />
            ) : (
              <div className="text-2xl font-bold text-error-600">
                {t.rewards.dayNumber.replace('{day}', deliverySettings.vendor_overdue_day)}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-secondary-200/60 bg-secondary-50/50 p-4  ">
            <label className="block text-sm font-medium text-secondary-700  mb-1">
              {t.rewards.clientDeliveryDaysLabel}
            </label>
            <p className="text-xs text-secondary-500  mb-3">{t.rewards.clientDeliveryDaysDesc}</p>
            {editingDeliverySettings ? (
              <input
                type="number"
                min="1"
                value={localDeliverySettings.client_delivery_days}
                onChange={(e) => setLocalDeliverySettings((prev) => ({
                  ...prev,
                  client_delivery_days: parseInt(e.target.value, 10) || 1,
                }))}
                className="input-field"
              />
            ) : (
              <div className="text-2xl font-bold text-orange-600">
                {deliverySettings.client_delivery_days} {t.rewards.daysUnit}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rewards Catalog */}
      <div className="card-premium p-6 max-lg:p-4">
        <div className="mb-6 flex flex-col gap-3 max-lg:items-stretch lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 flex items-center">
              <GiftIcon className="w-5 h-5 text-orange-500 mr-2" />
              {t.rewards.rewardsCatalog}
            </h3>
            <p className="text-sm text-secondary-600 mt-1">
              {t.rewards.manageRewardsRedeem}
            </p>
          </div>
          <Button variant="primary" size="default" onClick={() => setShowRewardModal(true)} icon={PlusIcon}>
            {t.rewards.addReward}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {rewards.map((reward) => (
            <motion.div
              key={reward.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="overflow-hidden rounded-2xl border border-orange-200/50 bg-gradient-to-br from-orange-50/50 to-amber-50/30   "
            >
              <div className="relative aspect-[6/5] overflow-hidden bg-secondary-100 ">
                <RewardCardVisual reward={reward} />
                {reward.is_welcome_choice ? (
                  <span className="pointer-events-none absolute left-3 top-3 z-30 rounded-full bg-orange-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm">
                    {t.rewards.welcomeChoiceBadge}
                  </span>
                ) : null}
              </div>

              <div className="space-y-3 p-4">
                <div className="min-w-0">
                  <h4 className="font-semibold text-secondary-900 ">{reward.name}</h4>
                  <p className="mt-1 line-clamp-2 text-sm text-secondary-600 ">{reward.description}</p>
                </div>

                <div className="flex items-center justify-between border-t border-white/20 pt-3 ">
                  <span className="text-xs text-secondary-600 ">{t.rewards.pointsRequired}</span>
                  <span className="flex items-center font-bold text-orange-600">
                    <SparklesIcon className="mr-1 h-4 w-4" />
                    {reward.points_required}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingReward(reward)}
                    aria-label={t.rewards.editReward}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-secondary-200/70 px-2.5 py-2 text-xs font-semibold text-secondary-700 transition-colors hover:bg-white/60   sm:flex-none"
                  >
                    <PencilIcon className="h-4 w-4" />
                    <span>{t.rewards.editReward}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteReward(reward.id)}
                    aria-label={t.rewards.deleteReward}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-error-200/80 px-2.5 py-2 text-xs font-semibold text-error-700 transition-colors hover:bg-error-50   sm:flex-none"
                  >
                    <TrashIcon className="h-4 w-4" />
                    <span>{t.rewards.deleteReward}</span>
                  </button>
                </div>

                {!reward.is_active && (
                  <div>
                    <span className="inline-flex rounded-full bg-secondary-100 px-2 py-1 text-xs font-medium text-secondary-600">
                      {t.rewards.inactive}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          
          {rewards.length === 0 && (
            <div className="col-span-full">
              <EmptyState
                icon={GiftIcon}
                title={t.rewards.noRewardsYet}
                description={t.rewards.createFirstReward}
                action={
                  <Button variant="primary" onClick={() => setShowRewardModal(true)} icon={PlusIcon}>
                    {t.rewards.createReward}
                  </Button>
                }
              />
            </div>
          )}
        </div>
      </div>

      {/* Create Reward Modal */}
      <Modal
        isOpen={showRewardModal}
        onClose={() => {
          setShowRewardModal(false);
          setNewReward(EMPTY_REWARD);
          setRewardFieldErrors({});
        }}
        title={t.rewards.createNewReward}
        size="large"
        footer={
          <ModalFooter>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&_button]:w-full sm:[&_button]:w-auto">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowRewardModal(false);
                  setNewReward(EMPTY_REWARD);
                  setRewardFieldErrors({});
                }}
              >
                {t.rewards.cancel}
              </Button>
              <Button variant="primary" onClick={handleCreateReward}>
                {t.rewards.createReward}
              </Button>
            </div>
          </ModalFooter>
        }
      >
        <RewardFormFields
          reward={newReward}
          onChange={(patch) => {
            setNewReward((prev) => ({ ...prev, ...patch }));
            setRewardFieldErrors((prev) => {
              const next = { ...prev };
              Object.keys(patch).forEach((key) => {
                if (next[key]) next[key] = null;
              });
              return next;
            });
          }}
          t={t}
          layout="modal"
          errors={rewardFieldErrors}
        />
      </Modal>

      {/* Edit Reward Modal */}
      <Modal
        isOpen={editingReward !== null}
        onClose={() => {
          setEditingReward(null);
          setRewardFieldErrors({});
        }}
        title={t.rewards.editReward}
        size="large"
        footer={
          editingReward ? (
            <ModalFooter>
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&_button]:w-full sm:[&_button]:w-auto">
                <Button variant="ghost" onClick={() => setEditingReward(null)}>
                  {t.rewards.cancel}
                </Button>
                <Button variant="primary" onClick={handleUpdateReward}>
                  {t.rewards.update}
                </Button>
              </div>
            </ModalFooter>
          ) : null
        }
      >
        {editingReward && (
          <RewardModalShell>
            <RewardFormFields
              reward={editingReward}
              onChange={(patch) => {
                setEditingReward((prev) => ({ ...prev, ...patch }));
                setRewardFieldErrors((prev) => {
                  const next = { ...prev };
                  Object.keys(patch).forEach((key) => {
                    if (next[key]) next[key] = null;
                  });
                  return next;
                });
              }}
              t={t}
              layout="modal"
              errors={rewardFieldErrors}
            />

            <label className="flex items-center rounded-2xl border border-secondary-200/70 bg-secondary-50/50 px-4 py-3  ">
              <Checkbox
                id="reward-active-edit"
                checked={editingReward.is_active}
                onChange={(checked) => setEditingReward((prev) => ({ ...prev, is_active: checked }))}
                label={t.rewards.active}
              />
            </label>
          </RewardModalShell>
        )}
      </Modal>
    </div>
  );
}

export default RewardsConfig;

